/**
 * VENDORED from canonical: `/app/shared-repo/web-components/components/AuthGate.jsx`
 * Round-1+ primitive (added 2026-05-22 cycle-5c+13 Phase B-1 Option B extraction).
 * Canon binding: `architecture-canon/INTERNAL_TOOLS/mp-roadmap.md §8 shared primitives`.
 * DO NOT edit this file directly — edit canonical and re-vendor.
 */
import { useEffect, useState, createContext, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { SignIn, ArrowRight, Warning, Compass } from "@phosphor-icons/react";

export const AuthedContext = createContext(null);
export function useAuthedUser() {
  const v = useContext(AuthedContext);
  if (!v) throw new Error("useAuthedUser must be used inside AuthGate");
  return v;
}

export default function AuthGate({
  children,
  apiClient,
  appName = "Roadmap",
  appSubLabel = "Master Planner",
  signInDescription = "Sign in with your LiftTech identity to view the live canon roadmap.",
  authCheckEndpoint = "/auth/check",
}) {
  const {
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    logout,
    getIdTokenClaims,
  } = useAuth0();
  const [checking, setChecking] = useState(false);
  const [authResult, setAuthResult] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!apiClient) {
      setAuthResult({
        authorized: false,
        email: user?.email,
        reason: "AuthGate misconfigured: no apiClient prop provided",
      });
      return;
    }
    let cancelled = false;
    setChecking(true);

    (async () => {
      try {
        const claims = await getIdTokenClaims();
        const rawToken = claims?.__raw;
        if (!rawToken) {
          if (!cancelled)
            setAuthResult({
              authorized: false,
              email: user?.email,
              reason: "No ID token in claims (re-sign-in may help)",
            });
          return;
        }
        apiClient.defaults.headers.common["Authorization"] = `Bearer ${rawToken}`;
        const res = await apiClient.post(authCheckEndpoint, { token: rawToken });
        if (!cancelled) setAuthResult(res.data);
      } catch (e) {
        if (!cancelled)
          setAuthResult({
            authorized: false,
            email: user?.email,
            reason: `Backend check failed: ${e.message}`,
          });
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, getIdTokenClaims, apiClient, authCheckEndpoint]);

  if (isLoading || checking) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "hsl(var(--background))" }}
      >
        <div className="flex items-center gap-3" style={{ color: "hsl(var(--muted-foreground))" }}>
          <div
            className="h-4 w-4 rounded-full border-2 animate-spin"
            style={{ borderColor: "hsl(var(--primary))", borderRightColor: "transparent" }}
          />
          <span className="text-sm">Checking sign-in…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <SignInLanding
        appName={appName}
        appSubLabel={appSubLabel}
        signInDescription={signInDescription}
        onSignIn={() => loginWithRedirect()}
      />
    );
  }

  if (authResult && !authResult.authorized) {
    return (
      <NotAuthorizedScreen
        email={authResult.email || user?.email}
        reason={authResult.reason}
        onSignOut={() => logout({ logoutParams: { returnTo: window.location.origin } })}
      />
    );
  }

  if (authResult && authResult.authorized) {
    return (
      <AuthedContext.Provider
        value={{
          user: authResult,
          signOut: () => logout({ logoutParams: { returnTo: window.location.origin } }),
          bootstrapNotice: authResult.reason === "bootstrap_mode_no_allowlist",
        }}
      >
        {children}
      </AuthedContext.Provider>
    );
  }

  return null;
}

function SignInLanding({ appName, appSubLabel, signInDescription, onSignIn }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "hsl(var(--background))" }}
      data-testid="mp-sign-in-landing"
    >
      <div className="surface-card max-w-md w-full p-8 text-center">
        <Compass
          size={48}
          weight="duotone"
          className="mx-auto mb-4"
          style={{ color: "hsl(var(--primary))" }}
        />
        <h1
          className="text-2xl font-semibold mb-2"
          style={{ color: "hsl(var(--foreground))" }}
        >
          {appName}
        </h1>
        <p
          className="text-mono mb-4"
          style={{ fontSize: "0.7rem", color: "hsl(var(--muted-foreground))" }}
        >
          {appSubLabel}
        </p>
        <p
          className="text-sm leading-relaxed mb-6"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          {signInDescription}
        </p>
        <button
          type="button"
          onClick={onSignIn}
          className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2"
          style={{
            background: "hsl(var(--primary))",
            color: "hsl(var(--primary-foreground))",
          }}
          data-testid="mp-sign-in-btn"
        >
          <SignIn size={16} />
          Sign in
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function NotAuthorizedScreen({ email, reason, onSignOut }) {
  const isAllowlistIssue = /MP_ALLOWED_EMAILS|allowlist|allow.list/i.test(reason || "");
  const headline = isAllowlistIssue
    ? "You're signed in, but not on the allowlist"
    : "Sign-in validated, but server couldn't accept the token";
  const helpText = isAllowlistIssue ? (
    <>
      MP Roadmap is staff-internal. Your Auth0 identity was validated, but{" "}
      <code
        className="text-mono px-1 py-0.5 rounded"
        style={{
          fontSize: "0.8em",
          background: "hsl(var(--muted))",
          color: "hsl(var(--foreground))",
        }}
      >
        {email}
      </code>{" "}
      is not in <code className="text-mono" style={{ fontSize: "0.8em" }}>MP_ALLOWED_EMAILS</code>.
    </>
  ) : (
    <>
      The Auth0 sign-in itself worked — your identity is{" "}
      <code
        className="text-mono px-1 py-0.5 rounded"
        style={{
          fontSize: "0.8em",
          background: "hsl(var(--muted))",
          color: "hsl(var(--foreground))",
        }}
      >
        {email || "(unknown)"}
      </code>{" "}
      — but the backend rejected the token. This is usually a config/setup issue, not an allowlist issue.
    </>
  );
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "hsl(var(--background))" }}
      data-testid="mp-not-authorized"
    >
      <div
        className="surface-card max-w-lg w-full p-8"
        style={{ borderColor: "hsl(var(--status-warning) / 0.4)" }}
      >
        <Warning
          size={32}
          className="mb-3"
          style={{ color: "hsl(var(--status-warning))" }}
        />
        <h1
          className="text-xl font-semibold mb-2"
          style={{ color: "hsl(var(--foreground))" }}
        >
          {headline}
        </h1>
        <p
          className="text-sm leading-relaxed mb-3"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          {helpText}
        </p>
        <p
          className="text-sm leading-relaxed mb-4"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          {isAllowlistIssue ? "Ask the Owner to add your email and re-sign-in." : "Tell MP this error message:"} <strong>{reason}</strong>
        </p>
        <button
          type="button"
          onClick={onSignOut}
          className="text-sm underline"
          style={{ color: "hsl(var(--primary))" }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
